import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  Tabs,
  Tab,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { format, subDays } from 'date-fns';

// API Service - Inline definition to match original
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5010';

const salesApi = {
  getTeamScorecard: async (customDay: string | null = null, period: string = 'currentWeek') => {
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const dayParam = customDay ? `&day=${customDay}` : '';
    const periodParam = period ? `&period=${period}` : '';
    
    const response = await fetch(
      `${API_BASE_URL}/api/scorecard/team?userId=${user.id}${dayParam}${periodParam}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    
    if (!response.ok) {
      throw new Error('Failed to fetch team scorecard');
    }
    
    return response.json();
  },
};

// TypeScript Interfaces
interface MetricData {
  actual: number;
  goal: number;
  color: 'green' | 'yellow' | 'red' | 'grey';
  actualFormatted?: string;
  goalFormatted?: string;
}

interface PeriodData {
  calls?: MetricData;
  contacts?: MetricData;
  loginTime?: MetricData;
  utilization?: MetricData;
  sales?: MetricData;
  sph?: MetricData;
}

interface ScorecardItem {
  userId: number;
  username: string;
  attributionId: string;
  yesterday?: PeriodData;
  currentWeek?: PeriodData;
  previousWeek?: PeriodData;
  currentMonth?: PeriodData;
  previousMonth?: PeriodData;
}

interface TeamScorecardResponse {
  scorecards: ScorecardItem[];
}

type SortOrder = 'asc' | 'desc';
type SortableField = 'username' | 'calls' | 'contacts' | 'loginTime' | 'utilization' | 'sales' | 'sph';
type PeriodType = 'currentWeek' | 'previousDay' | 'previousWeek' | 'currentMonth' | 'previousMonth';
type DisplayPeriod = 'yesterday' | 'currentWeek';
type ColorCode = 'green' | 'yellow' | 'red' | 'grey';
type ChipColorType = 'success' | 'warning' | 'error' | 'default';

const TeamScorecard: React.FC = () => {
  const [order, setOrder] = useState<SortOrder>('asc');
  const [orderBy, setOrderBy] = useState<SortableField>('username');
  const [tabValue, setTabValue] = useState<number>(0); // 0 = Yesterday, 1 = Current Week
  const [selectedDay, setSelectedDay] = useState<string>(format(subDays(new Date(), 1), 'yyyy-MM-dd')); // Initialize to yesterday's date
  const [period, setPeriod] = useState<PeriodType>('currentWeek'); // currentWeek, previousWeek, currentMonth, previousMonth

  const {
    data: scorecardData,
    isLoading,
    error,
  } = useQuery<TeamScorecardResponse>({
    queryKey: tabValue === 0 
      ? ['teamScorecard', 'daily', selectedDay]
      : ['teamScorecard', 'period', period],
    queryFn: () => salesApi.getTeamScorecard(
      tabValue === 0 ? selectedDay : null,
      period
    ),
    refetchInterval: 5 * 60 * 1000,
  });

  const scorecards: ScorecardItem[] = scorecardData?.scorecards || [];

  // Color mapping
  const getCellStyle = (colorCode?: ColorCode): React.CSSProperties => {
    switch (colorCode) {
      case 'green':
        return { backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 'bold' };
      case 'yellow':
        return { backgroundColor: '#fff3e0', color: '#ef6c00', fontWeight: 'bold' };
      case 'red':
        return { backgroundColor: '#ffebee', color: '#c62828', fontWeight: 'bold' };
      case 'grey':
      default:
        return { backgroundColor: '#f5f5f5', color: '#616161' };
    }
  };

  const getChipColor = (colorCode?: ColorCode): ChipColorType => {
    switch (colorCode) {
      case 'green':
        return 'success';
      case 'yellow':
        return 'warning';
      case 'red':
        return 'error';
      default:
        return 'default';
    }
  };

  // Sorting logic
  const handleSort = (property: SortableField): void => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Filter out Brian Peolman and sort
  const sortedScorecards: ScorecardItem[] = [...scorecards]
    .filter(scorecard => scorecard.username.toLowerCase() !== 'brian peolman')
    .sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      if (orderBy === 'username') {
        aValue = a.username;
        bValue = b.username;
      } else {
        // For metrics, sort by actual values
        const displayPeriod: DisplayPeriod = tabValue === 0 ? 'yesterday' : 'currentWeek';
        const metric = orderBy;
        aValue = a[displayPeriod]?.[metric]?.actual || 0;
        bValue = b[displayPeriod]?.[metric]?.actual || 0;
      }

      if (order === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number): void => {
    setTabValue(newValue);
  };

  const handleDayChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const newDate = event.target.value; // YYYY-MM-DD format
    setSelectedDay(newDate); // Always use the selected date
  };

  const handlePeriodChange = (event: any): void => {
    setPeriod(event.target.value as PeriodType);
  };

  if (isLoading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          Failed to load team scorecards. {(error as Error).message || 'Please check your connection and try again.'}
        </Alert>
      </Container>
    );
  }

  const displayPeriod: DisplayPeriod = tabValue === 0 ? 'yesterday' : 'currentWeek';
  
  // Dynamic period label based on selection
  const getPeriodLabel = (): string => {
    if (tabValue === 0) return "Daily Performance - Previous Day";
    
    switch (period) {
      case 'currentWeek':
        return 'Time Period Progress - Current Week';
      case 'previousWeek':
        return 'Time Period Progress - Previous Week';
      case 'currentMonth':
        return 'Time Period Progress - Current Month';
      case 'previousMonth':
        return 'Time Period Progress - Previous Month';
      default:
        return 'Time Period Progress - Current Week';
    }
  };
  
  const periodLabel: string = getPeriodLabel();

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h4" gutterBottom>
          Team Scorecards
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Overview of all Direct Meds users' performance metrics
        </Typography>
      </Box>

      {/* Tabs for Period Selection */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} indicatorColor="primary" textColor="primary">
          <Tab label="Daily Performance - Previous Day" />
          <Tab label="Time Period Progress" />
        </Tabs>
      </Paper>

      {/* Team Scorecard Table */}
      <Paper sx={{ p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
          <Typography variant="h6" fontWeight="bold">
            {periodLabel}
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            {tabValue === 0 && (
              <>
                <Typography variant="body2" color="textSecondary">
                  Select Day:
                </Typography>
                <TextField
                  type="date"
                  size="small"
                  value={selectedDay || format(subDays(new Date(), 1), 'yyyy-MM-dd')}
                  onChange={handleDayChange}
                  sx={{ minWidth: 180 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    max: format(new Date(), 'yyyy-MM-dd'), // Can't select future dates
                  }}
                />
              </>
            )}
            {tabValue === 1 && (
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Period</InputLabel>
                <Select
                  value={period}
                  label="Period"
                  onChange={handlePeriodChange}
                >
                  <MenuItem value="currentWeek">Current Week</MenuItem>
                  <MenuItem value="previousDay">Previous Day</MenuItem>
                  <MenuItem value="previousWeek">Previous Week</MenuItem>
                  <MenuItem value="currentMonth">Current Month</MenuItem>
                  <MenuItem value="previousMonth">Previous Month</MenuItem>
                </Select>
              </FormControl>
            )}
            <Chip
              label={`${scorecards.length} Users`}
              color="primary"
              variant="outlined"
            />
          </Box>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'username'}
                    direction={orderBy === 'username' ? order : 'asc'}
                    onClick={() => handleSort('username')}
                  >
                    Agent Name
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={orderBy === 'calls'}
                    direction={orderBy === 'calls' ? order : 'asc'}
                    onClick={() => handleSort('calls')}
                  >
                    Calls
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={orderBy === 'contacts'}
                    direction={orderBy === 'contacts' ? order : 'asc'}
                    onClick={() => handleSort('contacts')}
                  >
                    Contacts
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={orderBy === 'loginTime'}
                    direction={orderBy === 'loginTime' ? order : 'asc'}
                    onClick={() => handleSort('loginTime')}
                  >
                    Login Time
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={orderBy === 'utilization'}
                    direction={orderBy === 'utilization' ? order : 'asc'}
                    onClick={() => handleSort('utilization')}
                  >
                    Utilization
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={orderBy === 'sales'}
                    direction={orderBy === 'sales' ? order : 'asc'}
                    onClick={() => handleSort('sales')}
                  >
                    Sales
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={orderBy === 'sph'}
                    direction={orderBy === 'sph' ? order : 'asc'}
                    onClick={() => handleSort('sph')}
                  >
                    SPH
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">Overall Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedScorecards.length > 0 ? (
                sortedScorecards.map((scorecard) => {
                  const data: PeriodData | undefined = scorecard[displayPeriod];
                  
                  // Calculate overall status (majority color)
                  const colors: (ColorCode | undefined)[] = [
                    data?.calls?.color,
                    data?.contacts?.color,
                    data?.loginTime?.color,
                    data?.utilization?.color,
                    data?.sales?.color,
                    data?.sph?.color,
                  ];
                  const colorCounts = colors.reduce((acc: Record<string, number>, color) => {
                    if (color) {
                      acc[color] = (acc[color] || 0) + 1;
                    }
                    return acc;
                  }, {});
                  const overallColor: ColorCode = (Object.entries(colorCounts).sort((a, b) => b[1] - a[1])[0]?.[0] as ColorCode) || 'grey';

                  return (
                    <TableRow key={scorecard.userId} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {scorecard.username}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {scorecard.attributionId}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={getCellStyle(data?.calls?.color)}>
                        {data?.calls?.actual || 0} / {data?.calls?.goal || 0}
                      </TableCell>
                      <TableCell align="center" sx={getCellStyle(data?.contacts?.color)}>
                        {data?.contacts?.actual || 0} / {data?.contacts?.goal || 0}
                      </TableCell>
                      <TableCell align="center" sx={getCellStyle(data?.loginTime?.color)}>
                        {data?.loginTime?.actualFormatted || '0:00'} / {data?.loginTime?.goalFormatted || (tabValue === 0 ? '6:00' : '30:00')}
                      </TableCell>
                      <TableCell align="center" sx={getCellStyle(data?.utilization?.color)}>
                        {data?.utilization?.actual || 0}% / {data?.utilization?.goal || 85}%
                      </TableCell>
                      <TableCell align="center" sx={getCellStyle(data?.sales?.color)}>
                        {data?.sales?.actual || 0} / {data?.sales?.goal || 0}
                      </TableCell>
                      <TableCell align="center" sx={getCellStyle(data?.sph?.color)}>
                        {data?.sph?.actual?.toFixed(2) || '0.00'} / {data?.sph?.goal || '0.35'}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={
                            overallColor === 'green' ? '✓ Compliant' :
                            overallColor === 'yellow' ? '⚠ Needs Attention' :
                            overallColor === 'red' ? '✗ Below Target' :
                            '- No Data'
                          }
                          color={getChipColor(overallColor)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="textSecondary">No scorecard data available</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Legend */}
      <Paper sx={{ p: 2, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Compliance Legend
        </Typography>
        <Box display="flex" gap={3} flexWrap="wrap">
          <Box display="flex" alignItems="center" gap={1}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#e8f5e9', border: '1px solid #4caf50', borderRadius: 1 }} />
            <Typography variant="body2">100% Compliant (Green)</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#fff3e0', border: '1px solid #ffa726', borderRadius: 1 }} />
            <Typography variant="body2">90-99.9% Compliant (Yellow)</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#ffebee', border: '1px solid #f44336', borderRadius: 1 }} />
            <Typography variant="body2">Below 90% (Red)</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#f5f5f5', border: '1px solid #9e9e9e', borderRadius: 1 }} />
            <Typography variant="body2">No Data (Grey)</Typography>
          </Box>
        </Box>
      </Paper>

      {/* Summary Stats */}
      <Paper sx={{ p: 2, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Team Summary
        </Typography>
        <Box display="flex" gap={3} flexWrap="wrap">
          <Chip
            label={`Total Agents: ${scorecards.length}`}
            color="primary"
            variant="outlined"
          />
          <Chip
            label={`Compliant: ${scorecards.filter(s => {
              const data = s[displayPeriod];
              const colors = [data?.calls?.color, data?.contacts?.color, data?.loginTime?.color, data?.utilization?.color, data?.sales?.color];
              return colors.filter(c => c === 'green').length >= 3;
            }).length}`}
            color="success"
            variant="outlined"
          />
          <Chip
            label={`Needs Attention: ${scorecards.filter(s => {
              const data = s[displayPeriod];
              const colors = [data?.calls?.color, data?.contacts?.color, data?.loginTime?.color, data?.utilization?.color, data?.sales?.color];
              return colors.includes('yellow') || colors.includes('red');
            }).length}`}
            color="warning"
            variant="outlined"
          />
        </Box>
      </Paper>
    </Container>
  );
};

export default TeamScorecard;
